import { ReactNode, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { ApiResponse } from 'src/api/types'
import { TipsLink } from 'src/components/Link'
import { ProfileEditPreviewModal } from 'src/components/ProfilePreview'
import { LoginUser, toPreview } from 'src/models/User'
import { shouldDisplayPaidPlanLabel } from 'src/models/User/role'
import { orange, safetyOrange, truewhite } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { GUIDE_LINK } from 'src/utils/routes'
import styled, { css } from 'styled-components'
import { LowerLeftMessageModal } from './Messages'
import { ProfileTabs } from './ProfileTabs'
import { FormDescription, FormTitle, Title } from './common'

type ProfileFormKey = keyof Omit<LoginUser, 'id' | 'plan'>

export type ProfileFormProps<T extends ProfileFormKey> = {
  user: LoginUser
  propertyKey: T
  submit: (formdata: LoginUser[T]) => Promise<ApiResponse>
  children: ReactNode
  title: string
  description?: string
  isProfile?: true
  displayTipsLink?: boolean
}

export type ModalDetail = {
  success: boolean
  messages: string[]
}

type ProfileFormFC = <T extends ProfileFormKey>(
  props: ProfileFormProps<T>,
) => JSX.Element

const getSuccessMessage = (propertyKey: String): string => {
  switch (propertyKey) {
    case 'profile':
    case 'company':
    case 'responsibilities':
      return 'プロフィールを更新しました'
    case 'needs':
      return 'ニーズを更新しました'
    case 'solutions':
      return 'ソリューションを更新しました'
    default:
      return ''
  }
}

export const ProfileForm: ProfileFormFC = ({
  user,
  propertyKey,
  submit,
  title,
  description,
  isProfile,
  displayTipsLink = true,
  children,
}) => {
  const initaliValues = user[propertyKey]
  const [previewData, setPreviewData] = useState<typeof initaliValues>()
  const [modalDetail, setModalDetail] = useState<ModalDetail>({
    success: false,
    messages: [],
  })
  const methods = useForm<any>({
    defaultValues: initaliValues,
  })
  const { handleSubmit, getValues, reset, setError } = methods
  const onPreview = () => setPreviewData(getValues())
  const onSubmit = handleSubmit(async (data) => {
    // NOTE: inputコンポーネントでバリデーションできない場合、以下でエラーを出す
    if (data.onlineMtgAvailable === null) {
      setError('onlineMtgAvailable', {
        message: 'オンラインアポイントは必須です',
      })
      return
    }
    if (!data.position && propertyKey === 'profile') {
      setError('position', { message: '役職は必須です' })
      return
    }

    const res = await submit(data)
    window.scroll({ top: 0, behavior: 'smooth' })

    if (res.error?.errorMessages) {
      setModalDetail({ success: false, messages: res.error.errorMessages })
      return
    }

    setModalDetail({
      success: true,
      messages: [getSuccessMessage(propertyKey)],
    })
  })

  useEffect(() => {
    if (!initaliValues || !Object.keys(initaliValues).length) return
    // SWR再取得によるリセットで、ユーザーが選択した画像(blob URL)が上書きされるのを防ぐ
    const currentIconSrc = getValues('iconSrc')
    if (currentIconSrc?.startsWith('blob')) {
      const values = {
        ...(initaliValues as Record<string, unknown>),
        iconSrc: currentIconSrc,
      }
      reset(values)
    } else reset(initaliValues)
  }, [initaliValues, reset, getValues])
  return (
    <>
      <ProfileFormTitleWrap displayTipsLink={displayTipsLink}>
        <ProfileFormTitle>
          <FormTitle>{title}</FormTitle>
          <FormDescription>{description}</FormDescription>
        </ProfileFormTitle>
        {((displayTipsLink && user.plan.paid) || user.plan.isAdminRole) && (
          <TipsLinkWrap>
            <TipsLink to={GUIDE_LINK} align="flex-end" />
          </TipsLinkWrap>
        )}
      </ProfileFormTitleWrap>
      {isProfile && <ProfileTabs />}
      <Form onSubmit={onSubmit}>
        <FormProvider {...methods}>{children}</FormProvider>
        <ButtonsDiv>
          <FormButton type="button" onClick={onPreview}>
            プレビュー
          </FormButton>
          <FormSubmitButton type="submit">更新する</FormSubmitButton>
        </ButtonsDiv>
      </Form>
      <LowerLeftMessageModal
        modalDetail={modalDetail}
        close={() => setModalDetail({ success: true, messages: [] })}
      />
      {previewData && (
        <ProfileEditPreviewModal
          user={toPreview({
            ...user,
            [propertyKey]: previewData,
          })}
          close={() => setPreviewData(undefined)}
          displayCertified={shouldDisplayPaidPlanLabel([
            user && user.plan.role,
          ])}
        />
      )}
    </>
  )
}

const ProfileFormTitleWrap = styled.div<{ displayTipsLink?: boolean }>`
  display: flex;
  align-items: center;
  max-width: 700px;
  margin: auto;
  ${({ displayTipsLink }) =>
    !displayTipsLink &&
    css`
      justify-content: center;
    `}
`
const ProfileFormTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`
const TipsLinkWrap = styled.div`
  flex: 1;
`

type SimpleFormProps<T> = {
  title: string
  description?: string
  submitLabel: string
  submit: (formdata: T) => Promise<ApiResponse>
  children: ReactNode
  created?: boolean
  succeededMessage?: string
  buttonDesign?: boolean
}

type SimpleFormFC = <T>(props: SimpleFormProps<T>) => JSX.Element

export const SimpleForm: SimpleFormFC = ({
  title,
  description,
  children,
  submitLabel,
  submit,
  created,
  succeededMessage,
  buttonDesign,
}) => {
  const [submitSucceeded, setSubmitSucceeded] = useState(created ? true : false)
  const [messages, setMessages] = useState<string[]>([])
  const methods = useForm<any>()
  const { handleSubmit } = methods
  const onSubmit = handleSubmit(async (data) => {
    const res = await submit(data)
    if (res.error?.errorMessages) {
      setMessages(res.error.errorMessages)
      setSubmitSucceeded(false)
      return
    }
    if (succeededMessage) {
      setMessages([succeededMessage])
      setSubmitSucceeded(true)
    }
  })
  return (
    <>
      <Title title={title} description={description || ''} />
      <Form onSubmit={onSubmit}>
        <LowerLeftMessageModal
          modalDetail={{
            success: submitSucceeded,
            messages: messages,
          }}
          close={() => setMessages([])}
        />
        <FormProvider {...methods}>{children}</FormProvider>
        {buttonDesign ? (
          <ButtonsDesignDiv>
            <SimpleFormDesignSubmitButton type="submit">
              {submitLabel}
            </SimpleFormDesignSubmitButton>
          </ButtonsDesignDiv>
        ) : (
          <ButtonsDiv>
            <SimpleFormSubmitButton type="submit">
              {submitLabel}
            </SimpleFormSubmitButton>
          </ButtonsDiv>
        )}
      </Form>
    </>
  )
}

const Form = styled.form`
  padding: 16px 10px;
  max-width: 700px;
  margin: auto;
`

const ButtonsDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 48px;
  justify-content: space-around;

  > button {
    margin: 0px 24px 24px 24px;
  }
`

const ButtonsDesignDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  > button {
    margin: -5px 0;
  }
`

export const FormButtonBase = styled.button`
  width: 100%;
  max-width: 280px;
  padding: 16px;
  border-radius: 5px;
  color: ${safetyOrange};
  font-size: 14px;
  font-weight: ${fontWeightBold};
  text-align: center;
  white-space: nowrap;
  border: solid 1px ${safetyOrange};
`
const FormButton = styled(FormButtonBase)`
  background-color: ${truewhite};
`

const FormSubmitButton = styled(FormButtonBase)`
  color: ${truewhite};
  background-color: ${safetyOrange};
`
const SimpleFormSubmitButton = styled(FormButtonBase)`
  color: ${truewhite};
  background-color: ${orange};
`

const SimpleFormDesignSubmitButton = styled.button`
  width: 160px;
  line-height: 1.4;
  padding: 10px 0;
  border: 1px solid ${orange};
  background-color: #f8e4d4;
  color: ${orange};
  font-weight: ${fontWeightBold};
  font-size: 14px;
  line-height: 20px;
  border-radius: 5px;
  text-align: center;
`
